'use client';

import React, { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { useLanguage } from '@/contexts/LanguageContext';

const TestimonialsSection = () => {
  const { t } = useLanguage();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const testimonials = [
  {
    name: 'María González',
    country: 'Spain',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e2c8ddd7-1772402438589.png",
    alt: 'Smiling Hispanic woman with wavy brown hair and white blouse',
    quote: 'In 8 months I went from working in an office I hated to having my own prosperous practice. Now I earn $6,500 monthly helping people transform their lives.',
    quoteEs: 'En 8 meses pasé de trabajar en una oficina que odiaba a tener mi propia práctica próspera. Ahora gano $6,500 mensuales ayudando a personas a transformar sus vidas.'
  },
  {
    name: 'Carmen Rodríguez',
    country: 'Mexico',
    image: "https://images.unsplash.com/photo-1713078582993-fdd86b1a2c1f",
    alt: 'Mature Mexican woman with warm smile and golden earrings',
    quote: 'As a single mother, I needed a stable income that allowed me to be present for my children. This program gave me the financial freedom and purpose I was looking for.',
    quoteEs: 'Como madre soltera, necesitaba un ingreso estable que me permitiera estar presente para mis hijos. Este programa me dio la libertad financiera y el propósito que buscaba.'
  },
  {
    name: 'Ana Sofía Herrera',
    country: 'Colombia',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e304e8e8-1772430777384.png",
    alt: 'Young Colombian woman with curly hair and radiant smile',
    quote: 'I always knew I had a special gift, but didn\'t know how to monetize it ethically. The program taught me to create abundance while maintaining my integrity.',
    quoteEs: 'Siempre supe que tenía un don especial, pero no sabía cómo monetizarlo éticamente. El programa me enseñó a crear abundancia manteniendo mi integridad.'
  },
  {
    name: 'Laura Martínez',
    country: 'Argentina',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f4bfc892-1763296428258.png",
    alt: 'Argentine woman with confident smile in professional setting',
    quote: 'The community and support system is unlike anything I\'ve experienced. I went from zero to $4,200 monthly in just 5 months.',
    quoteEs: 'La comunidad y el sistema de apoyo son como nada que haya experimentado. Pasé de cero a $4,200 mensuales en solo 5 meses.'
  },
  {
    name: 'Isabella Santos',
    country: 'Brazil',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18ad3186b-1773081267842.png",
    alt: 'Brazilian woman with bright smile and natural hair',
    quote: 'Neuroakashic™ technology is genuinely transformative. Being an ambassador means I get to share something I truly believe in while building real financial security.',
    quoteEs: 'La tecnología Neuroakashic™ es genuinamente transformadora. Ser embajadora significa que puedo compartir algo en lo que realmente creo mientras construyo seguridad financiera real.'
  }];


  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) setIsVisible(true);},
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => {if (intervalRef.current) clearInterval(intervalRef.current);};
  }, [testimonials.length]);

  const handleDotClick = (index: number) => {
    setActiveIndex(index);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
  };

  const active = testimonials[activeIndex];

  return (
    <section ref={sectionRef} id="testimonios" className="py-32 bg-[#F4F7FC]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-20">
          <div className="inline-flex items-center gap-2 mb-6">
            <div className="w-1.5 h-1.5 rounded-full bg-[#1D6FE8]" />
            <span className="text-sm font-body font-medium text-[#1D6FE8] tracking-wide uppercase">
              {t('testimonials.eyebrow')}
            </span>
          </div>
          <h2 className="font-display font-bold text-[#0A1628] leading-tight tracking-tight mb-6"
          style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
            {t('testimonials.headline')}{' '}
            <span className="gradient-text-blue">{t('testimonials.headlineAccent')}</span>
          </h2>
          <p className="font-body text-lg text-[#4A5568]">
            {t('testimonials.subtitle')}
          </p>
        </div>

        {/* Featured Testimonial */}
        <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="bg-white rounded-3xl border border-[#E8EDF5] shadow-card overflow-hidden mb-8">
            <div className="grid grid-cols-1 lg:grid-cols-5">
              {/* Quote side */}
              <div className="lg:col-span-3 p-10 lg:p-14 flex flex-col justify-center">
                <div className="flex gap-1 mb-6">
                  {[1, 2, 3, 4, 5].map((i) =>
                  <Icon key={i} name="StarIcon" size={18} className="text-[#C9A84C]" />
                  )}
                </div>
                <blockquote className="font-display text-xl lg:text-2xl text-[#0A1628] leading-relaxed font-medium mb-8" style={{ letterSpacing: '-0.01em' }}>
                  &ldquo;{active.quote}&rdquo;
                </blockquote>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl overflow-hidden bg-[#F4F7FC] flex-shrink-0">
                    <AppImage
                      src={active.image}
                      alt={active.alt}
                      className="w-full h-full object-cover" />
                    
                  </div>
                  <div>
                    <div className="font-display font-bold text-[#0A1628]">{active.name}</div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <Icon name="MapPinIcon" size={13} className="text-[#4A5568]" />
                      <span className="text-sm font-body text-[#4A5568]">{active.country}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Visual side */}
              <div className="lg:col-span-2 bg-gradient-to-br from-[#0A1628] to-[#1D3A6E] p-10 flex flex-col justify-center items-center text-center">
                <div className="w-20 h-20 rounded-2xl overflow-hidden bg-white/10 mb-6 mx-auto">
                  <AppImage
                    src={active.image}
                    alt={active.alt}
                    className="w-full h-full object-cover" />
                  
                </div>
                <div className="text-white/60 text-xs font-body uppercase tracking-widest mb-2">Neuroakashic™ Light Ambassador</div>
                <div className="text-white font-display font-bold text-lg mb-1">{active.name}</div>
                <div className="text-white/60 text-sm font-body mb-8">{active.country}</div>
                <div className="w-full h-px bg-white/10 mb-8" />
                <div className="text-[#C9A84C] text-3xl font-display font-bold mb-1">
                  {activeIndex === 0 ? '$6,500' : activeIndex === 1 ? '$4,800' : activeIndex === 2 ? '$7,200' : activeIndex === 3 ? '$4,200' : '$5,100'}
                </div>
                <div className="text-white/60 text-xs font-body">Monthly Income</div>
              </div>
            </div>
          </div>

          {/* Thumbnail strip */}
          <div className="flex items-center justify-center gap-4 flex-wrap">
            {testimonials.map((t, i) =>
            <button
              key={i}
              onClick={() => handleDotClick(i)}
              className={`flex items-center gap-3 px-4 py-3 rounded-2xl border transition-all duration-350 ${
              i === activeIndex ?
              'bg-white border-[#1D6FE8] shadow-card' :
              'bg-white border-[#E8EDF5] hover:border-[#1D6FE8]/30'}`
              }>
              
                <div className="w-8 h-8 rounded-xl overflow-hidden bg-[#F4F7FC] flex-shrink-0">
                  <AppImage src={t.image} alt={t.alt} className="w-full h-full object-cover" />
                </div>
                <div className="text-left hidden sm:block">
                  <div className="text-xs font-display font-semibold text-[#0A1628]">{t.name}</div>
                  <div className="text-xs font-body text-[#4A5568]">{t.country}</div>
                </div>
                {i === activeIndex &&
              <div className="w-1.5 h-1.5 rounded-full bg-[#1D6FE8] ml-1" />
              }
              </button>
            )}
          </div>
        </div>
      </div>
    </section>);

};

export default TestimonialsSection;